import { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { OperationalPeriod } from "@/hooks/useOperationalPeriod";
import { usePDVOperators } from "@/hooks/usePDVOperators";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { supabase } from "@/integrations/supabase/client";
import { ShieldCheck, Lock } from "lucide-react";

interface ClosePeriodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (notes?: string) => Promise<void>;
  period: OperationalPeriod;
  showTotals?: boolean;
}

export function ClosePeriodDialog({
  open,
  onOpenChange,
  onConfirm,
  period,
  showTotals = true,
}: ClosePeriodDialogProps) {
  const { activeRestaurant } = useRestaurantContext();
  const { operators, isLoading: isLoadingOperators } = usePDVOperators(activeRestaurant?.id);

  const [step, setStep] = useState<"pin" | "confirm">("pin");
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [authenticatedOperatorName, setAuthenticatedOperatorName] = useState("");
  const [notes, setNotes] = useState("");
  const [isClosing, setIsClosing] = useState(false);

  // Real-time totals
  const [liveTotalSales, setLiveTotalSales] = useState(0);
  const [liveTotalOrders, setLiveTotalOrders] = useState(0);
  const [isLoadingTotals, setIsLoadingTotals] = useState(false);

  // Filter to only owner/manager operators
  const authorizedOperators = operators.filter(
    (op) => ["Proprietário", "Gerente"].includes(op.role)
  );

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep("pin");
      setPin("");
      setPinError("");
      setAuthenticatedOperatorName("");
      setNotes("");
    }
  }, [open]);

  // Fetch live totals when entering confirm step
  const fetchLiveTotals = useCallback(async () => {
    if (!period?.id) return;
    setIsLoadingTotals(true);
    try {
      const { data, error } = await supabase
        .from("orders")
        .select("total")
        .eq("period_id", period.id)
        .neq("status", "cancelled");

      if (error) throw error;

      const totalSales = (data || []).reduce((sum, o) => sum + (o.total || 0), 0);
      const totalOrders = (data || []).length;
      setLiveTotalSales(totalSales);
      setLiveTotalOrders(totalOrders);
    } catch (err) {
      console.error("Error fetching live totals:", err);
    } finally {
      setIsLoadingTotals(false);
    }
  }, [period?.id]);

  const handlePinDigit = (digit: string) => {
    if (pin.length >= 6) return;
    const newPin = pin + digit;
    setPin(newPin);
    setPinError("");

    // Auto-validate when PIN reaches 4+ digits
    if (newPin.length >= 4) {
      const matched = authorizedOperators.find((op) => op.pin === newPin);
      if (matched) {
        setAuthenticatedOperatorName(matched.name);
        setStep("confirm");
        fetchLiveTotals();
      } else if (newPin.length >= 6) {
        setPinError("PIN inválido ou sem permissão");
        setPin("");
      }
    }
  };

  const handlePinBackspace = () => {
    setPin((prev) => prev.slice(0, -1));
    setPinError("");
  };

  const handleConfirm = async () => {
    setIsClosing(true);
    await onConfirm(notes || undefined);
    setIsClosing(false);
    setNotes("");
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  const pinDots = Array.from({ length: 6 }, (_, i) => (
    <div
      key={i}
      className={`h-4 w-4 rounded-full border-2 transition-all ${
        i < pin.length
          ? "bg-primary border-primary"
          : "border-muted-foreground/30"
      }`}
    />
  ));

  const numpadButtons = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["", "0", "⌫"],
  ];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        {step === "pin" ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Autenticação Necessária
              </AlertDialogTitle>
              <AlertDialogDescription>
                Digite o PIN de um responsável (proprietário ou gerente) para fechar o período.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="py-4 space-y-6">
              {isLoadingOperators ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                </div>
              ) : authorizedOperators.length === 0 ? (
                <div className="text-center py-4 text-sm text-muted-foreground">
                  Nenhum operador com permissão (proprietário/gerente) possui PIN cadastrado.
                  Configure em Equipe → Editar funcionário.
                </div>
              ) : (
                <>
                  <div className="flex justify-center gap-3">{pinDots}</div>

                  {pinError && (
                    <p className="text-sm text-destructive text-center">{pinError}</p>
                  )}

                  <div className="grid grid-cols-3 gap-2 max-w-[240px] mx-auto">
                    {numpadButtons.flat().map((key, idx) =>
                      key === "" ? (
                        <div key={idx} />
                      ) : (
                        <Button
                          key={idx}
                          variant="outline"
                          className="h-12 text-lg font-semibold"
                          onClick={() =>
                            key === "⌫" ? handlePinBackspace() : handlePinDigit(key)
                          }
                        >
                          {key}
                        </Button>
                      )
                    )}
                  </div>
                </>
              )}
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Fechar Período</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="flex items-center gap-1 text-primary mb-1">
                  <ShieldCheck className="h-4 w-4" />
                  Autorizado por: {authenticatedOperatorName}
                </span>
                Você está prestes a fechar o período do dia{" "}
                <strong>
                  {format(new Date(period.reference_date + "T12:00:00"), "dd/MM/yyyy", { locale: ptBR })}
                </strong>
                . Após o fechamento, não será possível reabrir este período.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-4 py-4">
              {showTotals && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Total de Vendas</p>
                    <p className="text-lg font-bold">
                      {isLoadingTotals ? "..." : formatCurrency(liveTotalSales)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total de Pedidos</p>
                    <p className="text-lg font-bold">
                      {isLoadingTotals ? "..." : liveTotalOrders}
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Observações (opcional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Adicione observações sobre o fechamento..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel disabled={isClosing}>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirm} disabled={isClosing}>
                {isClosing ? "Fechando..." : "Confirmar Fechamento"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
